import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUrlState } from "@/lib/navState";
import { api } from "@/lib/api";
import { formatRupiah } from "@/lib/constants";
import { FileSpreadsheet, Printer, Search, RefreshCw, ReceiptText, HandCoins, Wallet, Factory } from "lucide-react";

const rp = (n) => formatRupiah(Number(n || 0));
const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString("en-CA"); };
const today = () => new Date().toLocaleDateString("en-CA");

const exportCSV = (name, cols, rows) => {
  const head = cols.map((c) => `"${c.label}"`).join(",");
  const body = rows.map((r) => cols.map((c) => `"${String(c.val(r) ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + head + "\n" + body], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${name}_${today()}.csv`; a.click();
};

const TABS = [
  { k: "billing", label: "Billing Register", icon: ReceiptText },
  { k: "aging", label: "AR Aging", icon: HandCoins },
  { k: "payment", label: "Payment Register", icon: Wallet },
  { k: "wip", label: "WIP Aging", icon: Factory },
];

const BAND = { NORMAL: "bg-emerald-100 text-emerald-700", WARNING: "bg-amber-100 text-amber-700", CRITICAL: "bg-orange-100 text-orange-700", OVERDUE: "bg-rose-100 text-rose-700", UNKNOWN: "bg-gray-200 text-gray-700" };
const STAT = { PAID: "bg-emerald-100 text-emerald-700", PARTIAL: "bg-amber-100 text-amber-700", OVERDUE: "bg-rose-100 text-rose-700", DUE_SOON: "bg-blue-100 text-blue-700", OPEN: "bg-gray-100 text-gray-600" };

export default function ReportControl() {
  const [tab, setTab] = useUrlState("tab", "billing", true);
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [q, setQ] = useUrlState("q", "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let r;
      if (tab === "billing") r = await api.get("/finance/reports/invoices", { params: { from, to } });
      else if (tab === "aging") r = await api.get("/finance/reports/receivable-aging");
      else if (tab === "payment") r = await api.get("/finance/reports/payments", { params: { from, to } });
      else r = await api.get("/finance/reports/wip");
      setData(r.data);
    } catch (e) { setData(null); }
    setLoading(false);
  }, [tab, from, to]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const all = data?.rows || [];
    if (!q.trim()) return all;
    const s = q.toLowerCase();
    return all.filter((r) => JSON.stringify(r).toLowerCase().includes(s));
  }, [data, q]);

  const showDates = tab === "billing" || tab === "payment";

  const COLS = {
    billing: [
      { label: "Date", val: (r) => r.invoice_date }, { label: "WO", val: (r) => r.wo_number }, { label: "Invoice", val: (r) => r.invoice_number },
      { label: "No Polisi", val: (r) => r.license_plate }, { label: "Model", val: (r) => r.vehicle }, { label: "Customer/Insurance", val: (r) => r.bill_to },
      { label: "SA", val: (r) => r.sa }, { label: "Jasa", val: (r) => r.jasa, rp: 1 }, { label: "Part", val: (r) => r.part, rp: 1 },
      { label: "Sublet", val: (r) => r.sublet, rp: 1 }, { label: "Klaim Asuransi", val: (r) => r.invoice_amount, rp: 1 }, { label: "OR Customer", val: (r) => r.or_customer, rp: 1 }, { label: "Total (Asuransi+OR)", val: (r) => r.total_with_or, rp: 1 }, { label: "Status", val: (r) => r.status, badge: STAT },
    ],
    payment: [
      { label: "Tgl Bayar", val: (r) => r.payment_date }, { label: "Invoice", val: (r) => r.invoice_number }, { label: "No Polisi", val: (r) => r.license_plate },
      { label: "Customer/Insurance", val: (r) => r.payer }, { label: "Nominal", val: (r) => r.amount, rp: 1 }, { label: "Cash/Bank", val: (r) => r.cash_bank },
      { label: "Metode", val: (r) => r.method }, { label: "Ref", val: (r) => r.reference }, { label: "Input By", val: (r) => r.input_by },
    ],
    aging: [
      { label: "No Polisi", val: (r) => r.license_plate }, { label: "Invoice", val: (r) => r.invoice_number }, { label: "Bill To", val: (r) => r.bill_to },
      { label: "Jatuh Tempo", val: (r) => r.due_date }, { label: "Outstanding", val: (r) => r.outstanding, rp: 1 }, { label: "Umur (hari)", val: (r) => r.overdue_days }, { label: "Bucket", val: (r) => r.bucket },
    ],
    wip: [
      { label: "WO", val: (r) => r.wo_number }, { label: "No Polisi", val: (r) => r.license_plate }, { label: "Customer", val: (r) => r.customer },
      { label: "Insurance", val: (r) => r.insurance }, { label: "SA", val: (r) => r.sa }, { label: "Foreman", val: (r) => r.foreman },
      { label: "Stage", val: (r) => r.stage }, { label: "Unit In", val: (r) => r.unit_in_date || "Missing" }, { label: "Dibuat", val: (r) => r.created_at }, { label: "Hari", val: (r) => (r.days_in ?? "—") },
      { label: "Nilai SPK", val: (r) => r.spk_value, rp: 1 }, { label: "Target Out", val: (r) => r.target_out }, { label: "Aging", val: (r) => r.band, badge: BAND },
    ],
  };
  const cols = COLS[tab];
  const clickable = (r) => r.claim_id;

  return (
    <div className="min-h-[calc(100vh-3rem)] w-full max-w-full overflow-x-hidden" data-testid="report-control-page">
      <div className="mx-auto min-w-0 max-w-[1500px]">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="font-heading text-xl font-bold text-gray-900 sm:text-2xl">Report &amp; Control</h1>
          <div className="flex items-center gap-1.5">
            <button data-testid="rc-export" onClick={() => exportCSV(tab, cols, rows)} className="flex h-9 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 hover:bg-gray-50"><FileSpreadsheet size={14} />Excel/CSV</button>
            <button data-testid="rc-print" onClick={() => window.print()} className="flex h-9 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 hover:bg-gray-50"><Printer size={14} />Print/PDF</button>
            <button data-testid="rc-refresh" onClick={load} className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /></button>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-1 border-b border-gray-200">
          {TABS.map((t) => (
            <button key={t.k} data-testid={`rc-tab-${t.k}`} onClick={() => setTab(t.k)} className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-semibold transition-colors ${tab === t.k ? "border-blue-600 text-blue-700" : "border-transparent text-gray-500 hover:text-gray-800"}`}><t.icon size={14} />{t.label}</button>
          ))}
        </div>

        {/* Summary cards */}
        {tab === "aging" && data && (
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6" data-testid="rc-aging-cards">
            <SumCard label="Total AR" value={rp(data.total)} tone="text-gray-900" />
            <SumCard label="Current" value={rp(data.buckets?.NOT_DUE)} tone="text-emerald-600" />
            <SumCard label="1-30 hari" value={rp(data.buckets?.["1-30"])} tone="text-blue-600" />
            <SumCard label="31-60 hari" value={rp(data.buckets?.["31-60"])} tone="text-amber-600" />
            <SumCard label="61-90 hari" value={rp(data.buckets?.["61-90"])} tone="text-orange-600" />
            <SumCard label="&gt;90 hari" value={rp(data.buckets?.["90+"])} tone="text-rose-600" />
          </div>
        )}
        {tab === "wip" && data && (
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-5" data-testid="rc-wip-cards">
            <SumCard label="Normal (0-7)" value={data.buckets?.NORMAL ?? 0} tone="text-emerald-600" />
            <SumCard label="Warning (8-14)" value={data.buckets?.WARNING ?? 0} tone="text-amber-600" />
            <SumCard label="Critical (15-30)" value={data.buckets?.CRITICAL ?? 0} tone="text-orange-600" />
            <SumCard label="Overdue (&gt;30)" value={data.buckets?.OVERDUE ?? 0} tone="text-rose-600" />
            <SumCard label="Unknown (No In-Date)" value={data.buckets?.UNKNOWN ?? 0} tone="text-gray-600" />
          </div>
        )}
        {tab === "billing" && data?.summary && (
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="rc-billing-cards">
            <SumCard label="Jml Invoice" value={data.summary.count} tone="text-gray-900" />
            <SumCard label="Total Invoice" value={rp(data.summary.total_invoice)} tone="text-gray-900" />
            <SumCard label="Total Dibayar" value={rp(data.summary.total_paid)} tone="text-emerald-600" />
            <SumCard label="Outstanding" value={rp(data.summary.total_outstanding)} tone="text-rose-600" />
          </div>
        )}
        {tab === "payment" && data?.summary && (
          <div className="mt-3 grid grid-cols-2 gap-2" data-testid="rc-payment-cards">
            <SumCard label="Jml Pembayaran" value={data.summary.count} tone="text-gray-900" />
            <SumCard label="Total Cash In" value={rp(data.summary.total_cash_in)} tone="text-emerald-600" />
          </div>
        )}

        {/* Read-only audit strip */}
        {(data?.audit || data?.summary?.audit) && (() => {
          const a = data.audit || data.summary.audit;
          const parts = Object.entries(a).filter(([, v]) => !Array.isArray(v) && typeof v !== "object").map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`);
          const flags = a.flagged_invoice_numbers && a.flagged_invoice_numbers.length ? ` · flagged (Unknown source): ${a.flagged_invoice_numbers.join(", ")}` : "";
          return <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-[11px] text-gray-600" data-testid="rc-audit"><span className="font-semibold text-gray-700">Audit:</span> {parts.join(" · ")}{flags}</div>;
        })()}

        {/* Filters */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {showDates && <>
            <input data-testid="rc-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 rounded-lg border border-gray-200 px-2 text-sm" />
            <span className="text-gray-400">—</span>
            <input data-testid="rc-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 rounded-lg border border-gray-200 px-2 text-sm" />
          </>}
          <div className="relative min-w-0 flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input data-testid="rc-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nopol / invoice / customer…" className="h-9 w-full rounded-lg border border-gray-200 pl-9 pr-3 text-sm" />
          </div>
        </div>

        {/* Table */}
        <div className="mt-3 overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="w-full min-w-[720px] text-xs">
            <thead className="sticky top-0 bg-gray-50"><tr>{cols.map((c) => <th key={c.label} className="whitespace-nowrap px-3 py-2 text-left font-semibold text-gray-600">{c.label}</th>)}</tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={cols.length} className="py-8 text-center text-gray-400">Memuat…</td></tr>
                : rows.length === 0 ? <tr><td colSpan={cols.length} className="py-8 text-center text-gray-400" data-testid="rc-empty">Belum ada data pada periode ini</td></tr>
                  : rows.slice(0, 500).map((r, i) => (
                    <tr key={i} data-testid="rc-row" onClick={() => clickable(r) && nav(`/claims/${r.claim_id}`)} className={`border-t border-gray-100 ${clickable(r) ? "cursor-pointer hover:bg-blue-50/50" : ""}`}>
                      {cols.map((c) => {
                        const v = c.val(r);
                        return <td key={c.label} className="whitespace-nowrap px-3 py-1.5 text-gray-800">
                          {c.badge ? <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${c.badge[v] || "bg-gray-100 text-gray-600"}`}>{v}</span> : c.rp ? rp(v) : (v ?? "-")}
                        </td>;
                      })}
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
        {rows.length > 500 && <p className="mt-2 text-center text-[11px] text-gray-400">Menampilkan 500 baris pertama. Gunakan filter atau Export untuk data lengkap.</p>}
      </div>
    </div>
  );
}

const SumCard = ({ label, value, tone }) => (
  <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm"><div className="text-[10px] font-medium uppercase tracking-wider text-gray-500" dangerouslySetInnerHTML={{ __html: label }} /><div className={`mt-0.5 truncate text-base font-bold ${tone}`}>{value}</div></div>
);
